import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Select } from '../../../shared/components/Select';
import { TextArea } from '../../../shared/components/TextArea';
import { useToast } from '../../../shared/components/toast/useToast';
import {
  useContactCadence,
  useGenerateBrandCard,
  useGenerateOutreachDraft,
  useLogOutreachSend,
  useOutreachBrandCard,
  useOutreachSends,
  useOutreachTemplates,
  useSetContactCadence,
} from '../hooks/useOutreach';
import {
  CHANNEL_LABELS,
  ENGAGEMENT_MODEL_LABELS,
  ORIGIN_CLASSES,
  ORIGIN_LABELS,
  OutreachChannel,
  OutreachEngagementModel,
  OutreachMessageOrigin,
} from '../types';
import { TemplateFormModal } from './TemplateFormModal';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const optionsFrom = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, title]) => ({ value, title }));

const NO_TEMPLATE = '';

function BrandCardSection({ contactId, canManage }: { contactId: string; canManage: boolean }) {
  const { data: card, isLoading, error } = useOutreachBrandCard(contactId);
  const generate = useGenerateBrandCard();

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Brand card</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Assembled from this contact's own details — regenerate after editing them.
          </p>
        </div>
        {canManage && (
          <Button
            variant="outline"
            size="sm"
            className="h-[40px]"
            onClick={() => generate.mutate(contactId)}
            disabled={generate.isPending}
            loading={generate.isPending}
          >
            {card ? 'Regenerate' : 'Generate brand card'}
          </Button>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-red-500">Could not load the brand card.</p>}
      {!isLoading && !error && !card && (
        <p className="text-sm text-muted-foreground">No brand card yet.</p>
      )}
      {card && (
        <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
          {card.headline && <p className="font-semibold text-foreground">{card.headline}</p>}
          {card.summary && <p className="text-sm text-foreground">{card.summary}</p>}
          {card.keySellingPoints && card.keySellingPoints.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-foreground space-y-0.5">
              {card.keySellingPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">Generated {formatDate(card.updatedAt)}</p>
        </div>
      )}
    </section>
  );
}

/**
 * Draft → review/edit → send yourself → record. There is deliberately no send button: osprey-api
 * does not deliver messages, and PE-2188 rules out any one-click auto-send. The message's
 * ORIGIN is where it started — an AI draft stays AI_DRAFTED however much it is edited, so the
 * send log can tell AI-assisted outreach from hand-written.
 */
function ComposeSection({ contactId }: { contactId: string }) {
  const { toast } = useToast();
  const [engagementModel, setEngagementModel] = useState<OutreachEngagementModel>(
    OutreachEngagementModel.WORKING_PREVIEW,
  );
  const [channel, setChannel] = useState<OutreachChannel>(OutreachChannel.WHATSAPP);
  const [templateId, setTemplateId] = useState(NO_TEMPLATE);
  const [body, setBody] = useState('');
  const [origin, setOrigin] = useState<OutreachMessageOrigin>(OutreachMessageOrigin.MANUAL);
  const [sendingAccount, setSendingAccount] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const { data: templates = [] } = useOutreachTemplates({
    engagementModel,
    channel,
    isActive: true,
  });
  const draft = useGenerateOutreachDraft();
  const logSend = useLogOutreachSend();

  const generate = async () => {
    const result = await draft.mutateAsync({ contactId, engagementModel });
    setBody(result.draftBody);
    setOrigin(OutreachMessageOrigin.AI_DRAFTED);
    setTemplateId(NO_TEMPLATE);
  };

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const template = templates.find((t) => t.id === id);
    if (!template) return;
    setBody(template.body);
    setOrigin(OutreachMessageOrigin.MANUAL);
  };

  const editBody = (value: string) => {
    setBody(value);
    // Typing into an empty box is writing from scratch, whatever came before.
    if (!body.trim()) setOrigin(OutreachMessageOrigin.MANUAL);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success('Message copied');
    } catch {
      toast.error('Could not copy — select the text and copy it manually.');
    }
  };

  const record = async () => {
    await logSend.mutateAsync({
      contactId,
      payload: {
        channel,
        sendingAccount: sendingAccount.trim(),
        engagementModel,
        messageBody: body.trim(),
        origin,
        ...(templateId ? { templateId } : {}),
      },
    });
    setBody('');
    setTemplateId(NO_TEMPLATE);
    setOrigin(OutreachMessageOrigin.MANUAL);
  };

  const canRecord = body.trim().length > 0 && sendingAccount.trim().length > 0;

  return (
    <section className="space-y-3 border-t border-border pt-5">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Compose a message</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Generate a brand-tailored cold message or start from a template, edit it, send it from
          your own account, then record it here. Nothing is sent automatically.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Select
          label="Engagement model"
          options={optionsFrom(ENGAGEMENT_MODEL_LABELS)}
          value={engagementModel}
          onChange={(value) => {
            setEngagementModel(value as OutreachEngagementModel);
            setTemplateId(NO_TEMPLATE);
          }}
        />
        <Select
          label="Channel"
          options={optionsFrom(CHANNEL_LABELS)}
          value={channel}
          onChange={(value) => {
            setChannel(value as OutreachChannel);
            setTemplateId(NO_TEMPLATE);
          }}
        />
        <Select
          label="Start from template"
          options={[
            {
              value: NO_TEMPLATE,
              title: templates.length ? 'None' : 'No active templates for this model',
            },
            ...templates.map((t) => ({ value: t.id, title: t.name })),
          ]}
          value={templateId}
          onChange={applyTemplate}
          disabled={templates.length === 0}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          className="h-[40px]"
          onClick={generate}
          disabled={draft.isPending}
          loading={draft.isPending}
        >
          {origin === OutreachMessageOrigin.AI_DRAFTED && body ? 'Regenerate' : 'Generate cold message'}
        </Button>
      </div>

      <div className="space-y-1">
        <TextArea
          label="Message"
          value={body}
          onChange={(e) => editBody(e.target.value)}
          rows={6}
          placeholder="Generate a draft, pick a template, or write your own…"
        />
        {body && (
          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${ORIGIN_CLASSES[origin]}`}>
            {ORIGIN_LABELS[origin]}
          </span>
        )}
      </div>

      <Input
        className="min-h-10 lg:min-h-0"
        label="Sent from (account)"
        value={sendingAccount}
        onChange={(e) => setSendingAccount(e.target.value)}
        placeholder={channel === OutreachChannel.WHATSAPP ? '+234 800 000 0000' : '@tcs.official'}
      />

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" className="h-[40px]" onClick={copy} disabled={!body}>
          Copy
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-[40px]"
          onClick={() => setSavingTemplate(true)}
          disabled={body.trim().length < 2}
        >
          Save as template
        </Button>
        <Button
          size="sm"
          className="h-[40px]"
          onClick={record}
          disabled={!canRecord || logSend.isPending}
          loading={logSend.isPending}
        >
          Record as sent
        </Button>
      </div>

      {savingTemplate && (
        <TemplateFormModal
          prefill={{
            engagementModel,
            channel,
            body: body.trim(),
            originAiDrafted: origin === OutreachMessageOrigin.AI_DRAFTED,
          }}
          onClose={() => setSavingTemplate(false)}
        />
      )}
    </section>
  );
}

function SendLogSection({ contactId }: { contactId: string }) {
  const { data: sends = [], isLoading, error } = useOutreachSends(contactId);
  const { data: templates = [] } = useOutreachTemplates();
  const templateName = new Map(templates.map((t) => [t.id, t.name]));

  return (
    <section className="space-y-3 border-t border-border pt-5">
      <h3 className="text-sm font-semibold text-foreground">Send history</h3>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-red-500">Could not load the send history.</p>}
      {!isLoading && !error && sends.length === 0 && (
        <p className="text-sm text-muted-foreground">No messages recorded yet.</p>
      )}
      {sends.length > 0 && (
        <ul className="space-y-3">
          {sends.map((send) => (
            <li key={send.id} className="text-sm text-foreground">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${ORIGIN_CLASSES[send.origin]}`}>
                  {ORIGIN_LABELS[send.origin]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(send.sentAt)} · {CHANNEL_LABELS[send.channel]} · from{' '}
                  {send.sendingAccount}
                  {send.templateId &&
                    ` · template: ${templateName.get(send.templateId) ?? 'removed template'}`}
                  {send.engagementModel && ` · ${ENGAGEMENT_MODEL_LABELS[send.engagementModel]}`}
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap">{send.messageBody}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** SetFollowUpContactOverrideDto bounds: @IsInt @Min(1) @Max(90); null clears. */
function CadenceOverrideSection({ contactId, canManage }: { contactId: string; canManage: boolean }) {
  const { data: override, isLoading } = useContactCadence(contactId);
  const setOverride = useSetContactCadence();

  if (isLoading) return null;
  return (
    <CadenceOverrideForm
      key={override?.intervalDays ?? 'none'}
      current={override?.intervalDays ?? null}
      canManage={canManage}
      pending={setOverride.isPending}
      onSave={(intervalDays) => setOverride.mutate({ contactId, intervalDays })}
    />
  );
}

function CadenceOverrideForm({
  current,
  canManage,
  pending,
  onSave,
}: {
  current: number | null;
  canManage: boolean;
  pending: boolean;
  onSave: (intervalDays: number | null) => void;
}) {
  const [value, setValue] = useState(current === null ? '' : String(current));
  const n = Number(value);
  const valid = Number.isInteger(n) && n >= 1 && n <= 90;

  return (
    <section className="space-y-2 border-t border-border pt-5">
      <h3 className="text-sm font-semibold text-foreground">Follow-up cadence</h3>
      <p className="text-xs text-muted-foreground">
        {current === null
          ? 'Using the cadence for this contact’s stage. Set days here to override it.'
          : `Overridden: due ${current} day${current === 1 ? '' : 's'} after the last message.`}
      </p>
      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <Input
            type="number"
            min={1}
            max={90}
            className="w-28 min-h-10 lg:min-h-0"
            aria-label="Follow-up override in days"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="days"
          />
          <Button
            size="sm"
            className="h-[40px]"
            disabled={!valid || n === current || pending}
            loading={pending}
            onClick={() => onSave(n)}
          >
            Save override
          </Button>
          {current !== null && (
            <Button
              variant="outline"
              size="sm"
              className="h-[40px]"
              disabled={pending}
              onClick={() => onSave(null)}
            >
              Clear
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The per-contact half of the outreach kit (PE-2188): brand card, the AI cold-message composer,
 * the send log and this contact's follow-up override. Mounted on ContactDetailPage behind
 * SALES_OUTREACH_VIEW; every write additionally needs SALES_OUTREACH_MANAGE.
 */
export function ContactOutreachPanel({
  contactId,
  canManage,
}: {
  contactId: string;
  canManage: boolean;
}) {
  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-5">
      <div>
        <h2 className="text-base font-semibold text-foreground">Outreach</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Brand card, message drafting and every message sent to this contact.
        </p>
      </div>
      <BrandCardSection contactId={contactId} canManage={canManage} />
      {canManage && <ComposeSection contactId={contactId} />}
      <SendLogSection contactId={contactId} />
      <CadenceOverrideSection contactId={contactId} canManage={canManage} />
    </div>
  );
}
